/**
 * 本地会话/偏好存储。
 * 只放"必须留在本机"的东西：年龄确认记忆、后台 token、收藏、跳过次数、昵称。
 * 不收集任何非必要个人信息（合规要求），服务端也不按用户维度落库。
 */

const KEYS = {
  ageOk: "baibaisex.ageOk",
  adminToken: "baibaisex.adminToken",
  favorites: "baibaisex.favorites",
  skips: "baibaisex.skips",
  name: "baibaisex.name",
  recentCards: "baibaisex.recentCards",
} as const;

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* 隐私模式下不可写，忽略 */
  }
}

/* ------------------------------------------------------------- 18+ 年龄门 */

export const ageGate = {
  passed: () => read(KEYS.ageOk) === "1",
  confirm: () => write(KEYS.ageOk, "1"),
  /** 后台"重置年龄门"或用户自己反悔时调用 */
  reset: () => write(KEYS.ageOk, null),
};

/* ------------------------------------------------------------- 后台 token */

export const adminSession = {
  token: () => read(KEYS.adminToken),
  save: (token: string) => write(KEYS.adminToken, token),
  clear: () => write(KEYS.adminToken, null),
};

/* --------------------------------------------------------------- 昵称 */

export const profile = {
  name: () => read(KEYS.name) ?? "",
  setName: (name: string) => write(KEYS.name, name.slice(0, 16)),
};

/* ------------------------------------------------------------- 卡牌收藏 */

export const favorites = {
  all: (): string[] => {
    try {
      const parsed = JSON.parse(read(KEYS.favorites) ?? "[]");
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
    } catch {
      return [];
    }
  },
  has: (id: string) => favorites.all().includes(id),
  toggle: (id: string): string[] => {
    const list = favorites.all();
    const next = list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
    write(KEYS.favorites, JSON.stringify(next));
    return next;
  },
};

/* ------------------------------------------------- 抽卡"短时间不重复" */

export const recentCards = {
  all: (): string[] => {
    try {
      const parsed = JSON.parse(read(KEYS.recentCards) ?? "[]");
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
    } catch {
      return [];
    }
  },
  push: (id: string, keep = 24): string[] => {
    const next = [id, ...recentCards.all().filter((item) => item !== id)].slice(0, keep);
    write(KEYS.recentCards, JSON.stringify(next));
    return next;
  },
  clear: () => write(KEYS.recentCards, null),
};

/* --------------------------------------------------------- 每日跳过次数 */

const today = (): string => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai" }).format(new Date());

export const skips = {
  used: (): number => {
    try {
      const parsed = JSON.parse(read(KEYS.skips) ?? "{}") as { day?: string; count?: number };
      return parsed.day === today() ? (parsed.count ?? 0) : 0;
    } catch {
      return 0;
    }
  },
  use: (): number => {
    const count = skips.used() + 1;
    write(KEYS.skips, JSON.stringify({ day: today(), count }));
    return count;
  },
  reset: () => write(KEYS.skips, null),
};

export const formatTime = (ms: number): string =>
  new Intl.DateTimeFormat("zh-CN", { hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(
    new Date(ms),
  );
